/**
 * Récapitulatif avant envoi — lot AV4 / AV7.
 *
 * ⛔ JAMAIS d'envoi silencieux : la sélection multiple ouvre ce récapitulatif, support par
 * support, et après l'envoi on affiche les biens ÉCARTÉS avec la raison rendue par la route
 * (`non_diffuse`, `non_modifie`, `support_sans_renvoi`) — on ne les avale pas.
 *
 * L'envoi réutilise POST /api/diffusion/bulk (lot AM2) : `activate` pour diffuser,
 * `update` pour renvoyer une annonce modifiée. ⛔ Aucune nouvelle route d'envoi.
 */
import React, { useState } from 'react';
import { bulkDiffusion } from '../../services/diffusionApi';
import { getSupportById } from '../../constants/diffusionConstants';

const RAISONS = {
  non_modifie: 'déjà à jour chez le portail',
  non_diffuse: 'pas en ligne sur ce support',
  support_sans_renvoi: 'support sans renvoi possible (site vitrine, CessionPME)',
  not_eligible: 'bien non éligible à la diffusion',
  quota_exceeded: 'quota du support atteint',
  // lot BP — le serveur nomme désormais ce qu'il écarte au lieu de le sauter en silence.
  deja_en_ligne: 'déjà en ligne',
  envoi_en_cours: 'publication déjà en cours',
  retrait_en_cours: 'retrait en cours, attendez la confirmation',
};

// lot BP — états qui interdisent un nouvel envoi (source : `/properties/diffusion-states`).
const ETATS_ENGAGES = ['en_ligne', 'modifie_non_renvoye', 'envoi_en_cours'];

/** Combien de biens de la sélection sont DÉJÀ engagés sur ce support, et combien refusés. */
const compterSupport = (biens, support) => {
  let engages = 0;
  let refuses = 0;
  for (const b of biens) {
    const etat = (b.diffusion || {})[support];
    if (ETATS_ENGAGES.includes(etat)) engages += 1;
    else if (etat === 'refuse') refuses += 1;
  }
  return { engages, refuses, total: biens.length };
};

export const DiffuserEnLotModal = ({ action, biens, supportsDisponibles, onClose, onTermine }) => {
  const [supports, setSupports] = useState(
    action === 'update'
      // Renvoi : seuls les supports qui savent renvoyer une annonce (lot AM2).
      ? supportsDisponibles.filter(s => ['seloger', 'leboncoin'].includes(s))
      : [],
  );
  const [enCours, setEnCours] = useState(false);
  const [resultats, setResultats] = useState(null);

  const titre = action === 'update'
    ? (biens.length > 1 ? `Renvoyer ${biens.length} mises à jour` : 'Renvoyer 1 mise à jour')
    : `Diffuser ${biens.length} bien${biens.length > 1 ? 's' : ''}`;

  const envoyer = async () => {
    setEnCours(true);
    try {
      const ids = biens.map(b => b.id);
      const parSupport = [];
      for (const support of supports) {
        const res = await bulkDiffusion(action, ids, [support]);
        parSupport.push({ support, ...res });
      }
      setResultats(parSupport);
      if (typeof onTermine === 'function') onTermine();
    } catch (e) {
      setResultats([{ support: '—', erreur: e?.response?.data?.detail || 'Envoi impossible' }]);
    } finally {
      setEnCours(false);
    }
  };

  // lot BP — plus aucun support n'a quelque chose à envoyer.
  const toutDejaDiffuse = action === 'activate' && supportsDisponibles.length > 0
    && supportsDisponibles.every(id => {
      const { engages, total } = compterSupport(biens, id);
      return total > 0 && engages === total;
    });

  const nomBien = (id) => {
    const b = biens.find(x => x.id === id);
    return b ? (b.reference || id) : id;
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4"
         data-testid="diffuser-lot-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2">
          <i className={`bi ${action === 'update' ? 'bi-arrow-repeat text-amber-600' : 'bi-broadcast text-emerald-600'} text-xl`} />
          <h2 className="font-['Outfit'] text-lg font-semibold text-slate-900">{titre}</h2>
        </div>

        {!resultats ? (
          <div className="p-5 space-y-4">
            <div>
              <p className="text-xs font-semibold tracking-wider uppercase text-slate-500 mb-2">
                Supports
              </p>
              <div className="space-y-1.5">
                {supportsDisponibles.map((id) => {
                  const sansRenvoi = action === 'update' && !['seloger', 'leboncoin'].includes(id);
                  // lot BP — sur « Diffuser », un support où TOUS les biens sont déjà engagés
                  // n'a rien à envoyer : la case le dit, au lieu d'un « 0 bien envoyé » final.
                  const { engages, refuses, total } = compterSupport(biens, id);
                  const tousEngages = action === 'activate' && engages === total && total > 0;
                  const desactive = sansRenvoi || tousEngages;
                  return (
                    <label key={id}
                           className={`flex items-start gap-2 text-sm ${desactive ? 'opacity-60' : 'cursor-pointer'}`}>
                      <input
                        type="checkbox"
                        disabled={desactive}
                        checked={supports.includes(id) || tousEngages}
                        onChange={(e) => setSupports(e.target.checked
                          ? [...supports, id]
                          : supports.filter(s => s !== id))}
                        className="w-4 h-4 mt-0.5 rounded border-slate-300 text-[#007BFF] focus:ring-2 focus:ring-blue-500/40"
                        data-testid={`diffuser-lot-support-${id}`}
                      />
                      <span className="flex-1">
                        <span>{getSupportById(id)?.label || id}</span>
                        {sansRenvoi && (
                          <span className="text-xs text-slate-500"> — pas de renvoi sur ce support</span>
                        )}
                        {action === 'activate' && (engages > 0 || refuses > 0) && (
                          <span className="block text-xs text-slate-500"
                                data-testid={`diffuser-lot-etat-${id}`}>
                            {tousEngages
                              ? (total === 1 ? 'déjà en ligne'
                                             : 'déjà en ligne ou en cours pour tous les biens')
                              : (engages > 0
                                  ? `déjà en ligne pour ${engages} bien${engages > 1 ? 's' : ''} sur ${total} — seuls les autres seront envoyés`
                                  : null)}
                            {!tousEngages && refuses > 0 && (
                              <>{engages > 0 ? ' · ' : ''}{`refusé pour ${refuses} bien${refuses > 1 ? 's' : ''} — sera renvoyé`}</>
                            )}
                          </span>
                        )}
                      </span>
                    </label>
                  );
                })}
                {toutDejaDiffuse && (
                  <p className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2"
                     data-testid="diffuser-lot-tout-diffuse">
                    <i className="bi bi-info-circle mr-1.5" />
                    {biens.length > 1
                      ? 'Ces biens sont déjà diffusés sur tous les supports.'
                      : 'Ce bien est déjà diffusé sur tous les supports.'}
                  </p>
                )}
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold tracking-wider uppercase text-slate-500 mb-2">
                {biens.length} bien{biens.length > 1 ? 's' : ''} concerné{biens.length > 1 ? 's' : ''}
              </p>
              <ul className="text-sm text-slate-700 space-y-0.5 max-h-40 overflow-y-auto"
                  data-testid="diffuser-lot-biens">
                {biens.map(b => (
                  <li key={b.id} className="flex items-baseline gap-2">
                    <span className="num text-xs font-semibold text-slate-500">{b.reference}</span>
                    <span className="truncate">{b.titre || ''}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ) : (
          <div className="p-5 space-y-3" data-testid="diffuser-lot-resultats">
            {resultats.map((r) => (
              <div key={r.support} className="border border-slate-200 rounded-lg p-3">
                <p className="text-sm font-semibold text-slate-900">
                  {getSupportById(r.support)?.label || r.support}
                </p>
                {r.erreur ? (
                  <p className="text-sm text-red-600 mt-1">{String(r.erreur)}</p>
                ) : (
                  <>
                    {/* lot BP — « 0 bien envoyé » tout seul ne disait pas POURQUOI. */}
                    {(r.succeeded || []).length > 0 ? (
                      <p className="text-sm text-emerald-700 mt-1">
                        {(r.succeeded || []).length} bien(s) envoyé(s)
                      </p>
                    ) : (
                      <p className="text-sm text-slate-700 mt-1" data-testid={`diffuser-lot-aucun-envoi-${r.support}`}>
                        Aucun envoi
                      </p>
                    )}
                    {(r.failed || []).length > 0 && (
                      <ul className="mt-1.5 text-xs text-slate-600 space-y-0.5">
                        {r.failed.map((f, i) => (
                          <li key={i}>
                            <span className="num font-semibold">{f.reference || nomBien(f.id)}</span>
                            {' — écarté : '}
                            {/* lot CC — le serveur nomme la cause (« Seuls les biens actifs
                                peuvent être diffusés. ») : on l'affiche telle quelle plutôt
                                qu'un « non éligible » qui n'explique rien. */}
                            {f.message || RAISONS[f.error] || f.error}
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="px-5 py-4 border-t border-slate-200 flex justify-end gap-2">
          <button type="button" onClick={onClose}
                  className="px-4 py-2 rounded-lg border border-slate-200 bg-white text-slate-700 text-sm font-medium hover:bg-slate-50"
                  data-testid="diffuser-lot-fermer">
            {resultats ? 'Fermer' : 'Annuler'}
          </button>
          {!resultats && (
            <button
              type="button"
              onClick={envoyer}
              disabled={enCours || supports.length === 0 || toutDejaDiffuse}
              data-testid="diffuser-lot-confirmer"
              className={`px-4 py-2 rounded-lg text-white text-sm font-semibold shadow-sm disabled:opacity-50 ${
                action === 'update' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-[#28A745] hover:bg-[#218838]'
              }`}
            >
              {enCours ? 'Envoi…' : (action === 'update' ? 'Envoyer les mises à jour' : 'Diffuser')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DiffuserEnLotModal;
